import { DataTypes, DataVariants } from "../../../data/DataManager";
import { InputTypes } from "../../../form/FormManager";
import { EMPTY_VALUE, FieldSchema } from "../../../Schema";
import { GridFilterTypes } from "../../../grid/AgGridManager";
import { Building } from "../external-sources/Buildings";
import { Floor, getAndProcessFloors, getFloor } from "../external-sources/Floors";
import { Site } from "../external-sources/Sites";
import { SchemaContext, KeyValueConstruct } from "../../common";
import { Modes } from "./mode";
import { LinkedInstance } from "../external-sources/LinkedInstances";
import { User } from "../external-sources/Users";

export const createFloor = (context: SchemaContext): FieldSchema => ({
  name: "floor",
  grid: {
    displayed: true,
    order: 34,
    filter: GridFilterTypes.TEXT,
    sortable: false,
  },
  graphql: { selection: "floor { key value }" },
  dataTransform: {
    fromGraphql: {
      getter: async ({ siteName, buildingLocation, floor }) => {
        const siteId = context.sites.find((s) => s.siteName === siteName)?.siteId;
        const buildingId = Number((buildingLocation as KeyValueConstruct)?.key);
        const floorId = Number((floor as KeyValueConstruct)?.key);
        if (siteId && buildingId && floorId) {
          const floorData = await getFloor(context.client, floorId);
          if (floorData && floorData.siteId === siteId && floorData.buildingsId === buildingId) {
            return { id: floorData.id, floorCode: floorData.floorCode, siteId: floorData.siteId, buildingsId: floorData.buildingsId };
          }
        }
        return EMPTY_VALUE;
      },
      fields: ["siteName", "buildingLocation"],
    },
    toGraphql: {
      getter: ({ floor }) => {
        if (floor) {
          return { key: (floor as Floor)?.id, value: (floor as Floor)?.floorCode };
        }
        return EMPTY_VALUE;
      },
    },
    // See buildingLocation: the stored key is text.
    toDb: {
      getter: ({ floor }) => {
        if (floor) {
          return { key: String((floor as Floor)?.id), value: (floor as Floor)?.floorCode };
        }
        return EMPTY_VALUE;
      },
    },
  },
  data: {
    type: DataTypes.OBJECT,
    variant: DataVariants.ENUM,
    options: {
      getter: async ({ siteName, buildingLocation }) => {
        // Both keys, not both objects — see buildingLocation's options for why.
        const siteId = (siteName as Site)?.siteId;
        const buildingId = (buildingLocation as Building)?.id;
        if (!siteId || !buildingId) {
          return [];
        }
        return (await getAndProcessFloors(context.client, siteId, buildingId))
          .map((f: Floor) => ({ value: f, label: f.floorCode }));
      },
      fields: ["siteName", "buildingLocation"],
    },
  },
  display: {
    displayed: true,
    translator: (value) => [(value as Floor).floorCode as string],
  },
  validation: { required: true },
  form: {
    label: "Floor",
    type: InputTypes.SELECT,
    disabled: {
      getter: ({ siteName, buildingLocation, linkedInstance, mode, currentUser }) => {
        return !siteName || !buildingLocation ||
        (
          [Modes.EDIT, Modes.BULK_EDIT].includes(mode as Modes) &&
          ["P1S Uni-Directional"].includes((linkedInstance as LinkedInstance)?.linkedInstance)
        ) && !(currentUser as User)?.isAdmin;
      },
      fields: ["siteName", "buildingLocation", "linkedInstance", "mode", "currentUser"],
    },
  },
});
